import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MessageSquare } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type UserFeedback } from '@/lib/queries/rca/mockData';

import {
  CommentForm,
  ExistingFeedback,
  RatingButtons,
  TeamFeedback,
} from './FeedbackComponents';

interface InvestigationFeedbackProps {
  existingFeedback?: UserFeedback[];
  onSubmitFeedback: (feedback: {
    rating: 'positive' | 'negative';
    comment?: string;
  }) => void;
}

export function InvestigationFeedback({
  existingFeedback = [],
  onSubmitFeedback,
}: InvestigationFeedbackProps): JSX.Element {
  const [selectedRating, setSelectedRating] = useState<
    'positive' | 'negative' | null
  >(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  const currentUserId = 'user-current';
  const userFeedback = existingFeedback.find(f => f.userId === currentUserId);

  const handleRatingSelect = (rating: 'positive' | 'negative'): void => {
    setSelectedRating(rating);
    setShowCommentForm(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (selectedRating === null) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmitFeedback({
        rating: selectedRating,
        comment: comment.trim() !== '' ? comment.trim() : undefined,
      });

      setSelectedRating(null);
      setComment('');
      setShowCommentForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    setSelectedRating(null);
    setComment('');
    setShowCommentForm(false);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          <FormattedMessage id="rca.feedback.title" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!userFeedback && !showCommentForm && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <FormattedMessage id="rca.feedback.rateInvestigation" />
            </p>
            <RatingButtons
              selectedRating={selectedRating}
              onRatingSelect={handleRatingSelect}
            />
          </div>
        )}

        {showCommentForm && selectedRating !== null && (
          <CommentForm
            selectedRating={selectedRating}
            comment={comment}
            onCommentChange={setComment}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        )}

        {Boolean(userFeedback) && <ExistingFeedback feedback={userFeedback!} />}

        {existingFeedback.length > 0 && (
          <TeamFeedback feedbackList={existingFeedback} />
        )}
      </CardContent>
    </Card>
  );
}
